import styled from '@emotion/styled';
import { useRef, useState, useMemo, useEffect } from 'react';
import { PencilSimple } from '@phosphor-icons/react';
import { AccessControl } from 'features/accessControl';
import { Tooltip } from 'components/tooltip';
import { useEscapeToClose, useRestoreFocusOnStay } from '../../../hooks';
import { useRowHeight } from '../../../providers/TableProvider';
import {
  useEditableContextOptional,
  useShouldSkipInlineCommit,
} from '../../OptimizedTableProvider';
import { DotsMenuButton } from '../../../components/DotsMenuButton';
import type { EditableTextCellProps } from '../../../components/types';
import { t } from '@lingui/macro';

interface DocWithCaret {
  caretPositionFromPoint?: (clientX: number, clientY: number) => { offset: number } | null;
}

/**
 * Resolve the character offset closest to (x, y) using the standardized
 * caretPositionFromPoint API (Firefox, modern Chromium, Safari ≥ 18.4). Returns
 * null when the browser doesn't support it — the caret then falls back to the end
 * of the value. The non-standard caretRangeFromPoint is intentionally not used.
 */
function caretOffsetFromPoint(x: number, y: number): number | null {
  const doc: DocWithCaret = document;
  if (typeof doc.caretPositionFromPoint === 'function') {
    return doc.caretPositionFromPoint(x, y)?.offset ?? null;
  }
  return null;
}

export const EditableTextCell = <T extends { id: number | string }>({
  onUpdate,
  placeholder = t`Enter value...`,
  error,
  validate,
  maxChars,
  editable = true,
  disabled = false,
  hideActions = false,
  readModeEl,
  context: { getValue, row, column },
}: EditableTextCellProps<T>) => {
  // getValue can be null for empty cells (e.g. an unset custom field); normalise to a
  // string so localValue/length checks are always safe.
  const value = getValue<string | null>() ?? '';
  const rowHeight = useRowHeight();
  const editableCtx = useEditableContextOptional();
  const editableState = editableCtx?.editableState ?? null;
  const setEditableState = editableCtx?.setEditableState;
  const hasEditableCtx = editableCtx != null;
  const [localIsEditing, setLocalIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const [localError, setLocalError] = useState<string | undefined>(undefined);
  // Character offset under the click that opened edit mode, so the caret can be
  // restored to where the user clicked (DL-59869). Null when resolved on an Edit
  // menu/keyboard open or in a browser without the caret-from-point APIs.
  const caretOffsetRef = useRef<number | null>(null);

  // Inside a table provider the editing state is owned by the shared editableState, so it
  // survives cell remounts — e.g. when the optimistic save of another cell rebuilds the
  // table columns and React remounts every cell (DL-60076). Outside a provider (unit
  // tests) we fall back to local state.
  const isActiveInCtx =
    editableState?.rowIndex === row.index && editableState?.columnId === column.id;
  const isEditing = hasEditableCtx ? isActiveInCtx : localIsEditing;

  // Latest "is this cell the active editor", read by save callbacks so a late
  // resolution does not clear the slot after another cell has taken over.
  const isActiveRef = useRef(isEditing);
  useEffect(() => {
    isActiveRef.current = isEditing;
  });

  // Skip the blur-save while a navigation is being confirmed. The guard raises the flag
  // synchronously — when the leave dialog opens (browser back), and, when the user clicks an
  // in-app nav link, in a capture-phase pointerdown BEFORE this blur fires — so the edit is
  // never sent and "Leave" / Esc discard it (DL-60097).
  const shouldSkipCommit = useShouldSkipInlineCommit();

  const enterEdit = (newLocalValue: string) => {
    setLocalValue(newLocalValue);
    setLocalError(undefined);
    if (hasEditableCtx) {
      setEditableState?.({ rowIndex: row.index, columnId: column.id });
    } else {
      setLocalIsEditing(true);
    }
  };

  const exitEdit = () => {
    if (hasEditableCtx) {
      // Only relinquish the shared editing slot if this cell still owns it. A functional
      // update (not isActiveRef, which stays stale until re-render) keeps this correct when
      // a just-clicked target cell claims the slot in the same tick: on a blur-save the
      // outgoing cell must not clear the slot the target already took (DL-60264/60076/60079).
      setEditableState?.(prev =>
        prev?.rowIndex === row.index && prev?.columnId === column.id
          ? { rowIndex: null, columnId: null }
          : prev
      );
    } else {
      setLocalIsEditing(false);
    }
  };

  const save = () => {
    // A leave-confirmation dialog is open (it stole focus and blurred us) — don't commit or
    // exit; the navigation guard owns this interaction and keeps the cell editing (DL-60097).
    if (shouldSkipCommit()) return;
    // Treat whitespace-only input as empty so it clears the field rather than
    // persisting blanks — which previously left Text custom fields un-editable (DL-60080).
    const normalized = localValue.trim() === '' ? '' : localValue;

    if (normalized === value) {
      setLocalError(undefined);
      exitEdit();
      return;
    }

    if (maxChars != null && normalized.length > maxChars) {
      // Over the limit — block the save. The inline over-limit error is derived from
      // localValue (see overLimitError below), so it is already visible.
      return;
    }

    const err = validate?.(normalized);
    if (err) {
      setLocalError(err);
      return;
    }

    onUpdate(normalized);
    setLocalError(undefined);
    exitEdit();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    }
  };

  useEscapeToClose(isEditing, () => {
    setLocalError(undefined);
    exitEdit();
  });

  const inputRef = useRef<HTMLInputElement | null>(null);

  // When edit mode opens, focus the input and place the caret where the user clicked
  // (per spec, DL-59869). The click offset is captured in handleCellClick; if it's
  // unavailable (Edit menu/keyboard open, or a browser without the caret-from-point
  // APIs) the caret falls back to the end of the value.
  //
  // Deferred to the next animation frame so the browser's native focus handling for
  // the originating click — whose target (the read element) is unmounted as we switch
  // into edit mode — does not steal focus back afterwards, which previously left the
  // field needing a second click.
  useEffect(() => {
    if (!isEditing) return undefined;
    const input = inputRef.current;
    if (!input) return undefined;
    const raf = requestAnimationFrame(() => {
      input.focus();
      const len = input.value.length;
      const offset = caretOffsetRef.current === null ? len : Math.min(caretOffsetRef.current, len);
      input.setSelectionRange(offset, offset);
      caretOffsetRef.current = null;
      // Focusing a long value can leave the input scrolled to the end, and
      // setSelectionRange's native scroll-into-view is unreliable inside an animation
      // frame after the table re-renders — so the caret can sit off-screen and look
      // missing. Align the horizontal scroll with the caret: clicks always land in the
      // first (visible) characters of a truncated cell, so show the start; the
      // end-fallback shows the tail (DL-59869).
      input.scrollLeft = offset === len ? input.scrollWidth : 0;
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when edit mode toggles
  }, [isEditing]);

  // After the leave-confirmation dialog closes via "Stay", MUI returns focus to whatever
  // opened the dialog (the nav link), not our input — pull focus back (DL-60097).
  useRestoreFocusOnStay(inputRef, isEditing);

  const displayValue = value || (editable ? '_' : '');
  const isInteractive = editable && !disabled;
  const showDotsMenu = isInteractive && !hideActions && row.original.id !== -1;
  // Over-limit validation is derived from the current input (not stored) so the error
  // always reflects what's typed and disappears the moment the value drops back under
  // maxChars — stored error state could otherwise get stuck after a blur/save (DL-60080).
  const overLimitError =
    isEditing && maxChars != null && localValue.length > maxChars
      ? t`${localValue.length - maxChars} characters too many. Try removing some text to continue.`
      : undefined;
  const activeError = localError || overLimitError || error;

  const menuOptions = useMemo(
    () => [
      {
        label: 'Edit',
        icon: <PencilSimple data-testid="pencil_simple_icon" size={16} />,
        onClick: () => enterEdit(value),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- enterEdit is stable across renders
    [value]
  );

  const handleCellClick = isInteractive
    ? (e: React.MouseEvent<HTMLDivElement>) => {
        caretOffsetRef.current = caretOffsetFromPoint(e.clientX, e.clientY);
        enterEdit(value);
      }
    : undefined;

  const readFallback = readModeEl ? (
    <ReadModeElWrapper $editable={false} $rowHeight={rowHeight}>
      {readModeEl}
    </ReadModeElWrapper>
  ) : (
    <ReadWrapper
      $rowHeight={rowHeight}
      $editable={false}
      $disabled={disabled}
      title={activeError || undefined}
    >
      {displayValue}
    </ReadWrapper>
  );

  if (isEditing && !disabled) {
    return (
      <AccessControl write fallback={readFallback}>
        <Tooltip
          title={activeError ?? ''}
          open={!!activeError}
          placement="bottom-start"
          arrow
          error
          followCursor={false}
        >
          <StyledInput
            ref={inputRef}
            value={localValue}
            placeholder={placeholder}
            error={!!activeError}
            title={activeError || undefined}
            onChange={e => {
              setLocalValue(e.target.value);
              if (localError) setLocalError(undefined);
            }}
            onBlur={save}
            onKeyDown={handleKeyDown}
          />
        </Tooltip>
      </AccessControl>
    );
  }

  const isPlaceholder = displayValue === '_';
  const useWrapTooltip = !isPlaceholder && displayValue.length > 40;
  const readContent = (
    <>
      {readModeEl ? (
        <ReadModeElWrapper
          $editable={isInteractive}
          $rowHeight={rowHeight}
          onClick={handleCellClick}
        >
          {readModeEl}
        </ReadModeElWrapper>
      ) : (
        <Tooltip
          disableFocusListener={isPlaceholder}
          disableHoverListener={isPlaceholder}
          disableInteractive={isPlaceholder || useWrapTooltip}
          wrapLongText={useWrapTooltip}
          title={displayValue}
        >
          <ReadWrapper
            $rowHeight={rowHeight}
            $editable={isInteractive}
            $disabled={disabled}
            title={activeError || undefined}
            onClick={handleCellClick}
          >
            {displayValue}
          </ReadWrapper>
        </Tooltip>
      )}
      {showDotsMenu && (
        <DotsMenuButtonWrapper>
          <DotsMenuButton menuOptions={menuOptions} />
        </DotsMenuButtonWrapper>
      )}
    </>
  );

  return (
    <AccessControl write fallback={readFallback}>
      {readContent}
    </AccessControl>
  );
};

const DotsMenuButtonWrapper = styled.div`
  max-width: 18px;
`;

const ReadModeElWrapper = styled.div<{ $editable: boolean; $rowHeight: number }>`
  display: block;
  width: 100%;
  min-width: 0;
  line-height: ${p => p.$rowHeight}px;
  padding: 0 ${p => p.theme.s.sm};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: ${p => (p.$editable ? 'pointer' : 'default')};
  &:hover {
    background-color: ${p => p.$editable && p.theme.palette.utility.grey[100]};
  }
`;

const ReadWrapper = styled.div<{ $rowHeight: number; $editable: boolean; $disabled: boolean }>`
  display: block;
  width: 100%;
  line-height: ${p => p.$rowHeight}px;
  padding: 0 ${p => p.theme.s.sm};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: ${p => (p.$editable ? 'pointer' : 'default')};
  ${p => (p.$disabled ? `background-color: ${p.theme.palette.bg.disabledSubtle};` : '')};
  ${p => (p.$disabled ? `color: ${p.theme.palette.text.disabledSubtle};` : '')};
  &:hover {
    background-color: ${p => p.$editable && p.theme.palette.utility.grey[100]};
  }
`;

const StyledInput = styled.input<{ error?: boolean }>`
  display: block;
  width: 100%;
  box-sizing: border-box;
  min-height: 40px;
  padding: 0 ${p => p.theme.s.sm};
  background: transparent;
  border: none;
  // a little hacky here because first row is positioned differently than subsequent rows, so moved the red line up one pixel until we fix that
  box-shadow: ${p =>
    p.error ? `inset 0 -1px 0 white, inset 0 -2px 0 ${p.theme.palette.border.errorSolid}` : 'none'};
  outline: none;
  font-family: inherit;
  font-size: inherit;
  color: ${p => (p.error ? p.theme.palette.fg.errorPrimary : 'inherit')};
`;
